import { Box, Button, DropdownMenu, HStack, Icon, ShimmerText, Text, VStack } from '@betterment/samba-react';

import { trackClick } from '../../../../features/track';
import { useDirectTradingAccountInflowOptions } from '../../../../Shared/hooks/useDirectTradingAccountInflowOptions/useDirectTradingAccountInflowOptions';
import { locationAssign } from '../../../../Shared/path-helpers';
import { urlBuilder } from '../../../../Shared/urlBuilder';

type Options = NonNullable<ReturnType<typeof useDirectTradingAccountInflowOptions>>;

const itemCss = { whiteSpace: 'nowrap', paddingY: 'space.100' };
const loadingItemCss = { ...itemCss, width: '300px' };

const LoadingItems = () => (
  <>
    <DropdownMenu.Item css={loadingItemCss}>
      <ShimmerText textStyle="text50" />
    </DropdownMenu.Item>
    <DropdownMenu.Item css={loadingItemCss}>
      <ShimmerText textStyle="text50" />
    </DropdownMenu.Item>
  </>
);

const Items: React.FC<{ options: Options; accountId: string }> = ({ options, accountId }) => {
  const { depositOption, transferOption } = options;

  const navigateToDepositSetup = () => {
    const subAccountId = depositOption.destinationLegacySubAccountId;
    const returnTo = urlBuilder.appAccount.absolute({ accountId });
    trackClick({
      name: 'Deposit',
      module: 'Add cash',
      flow: 'BuyOrder',
    });
    locationAssign(urlBuilder.appDepositSetup.absolute({ subAccountId, returnTo }));
  };

  const navigateToTransferSetup = ({ option }: { option: NonNullable<Options['transferOption']> }) => {
    const returnTo = window.location.pathname;
    const { sourceLegacySubAccountId: subAccountId, destinationLegacySubAccountId: destinationAccountId } = option;
    trackClick({
      name: 'Transfer',
      module: 'Add cash',
      flow: 'BuyOrder',
    });
    locationAssign(
      urlBuilder.showSubAccountTransferSetup.absolute({
        subAccountId,
        destination_account_id: destinationAccountId,
        returnTo,
      })
    );
  };

  return (
    <Box css={{ width: 'min-content' }}>
      <DropdownMenu.Item css={itemCss} onSelect={navigateToDepositSetup}>
        Deposit from a bank account
      </DropdownMenu.Item>
      {transferOption && (
        <DropdownMenu.Item css={itemCss} onSelect={() => navigateToTransferSetup({ option: transferOption })}>
          <VStack>
            <Text>Transfer from a Betterment account</Text>
            {transferOption.instantTransferEligible && (
              <HStack css={{ gap: 'space.50' }}>
                <Icon name="Lightning" size="xxsmall" color="icon.success" />
                <Text variant="text50" color="text.success">
                  Instant transfer available
                </Text>
              </HStack>
            )}
          </VStack>
        </DropdownMenu.Item>
      )}
    </Box>
  );
};

export const AddCashButton: React.FC<{ accountId: string }> = ({ accountId }) => {
  const options = useDirectTradingAccountInflowOptions({ accountId });

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild={true}>
        <Button
          variant="textPrimary"
          onClick={() => {
            trackClick({
              name: 'AddCash',
              module: 'BuyOrderForm',
              flow: 'BuyOrder',
            });
          }}
        >
          Add cash
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" side="bottom">
        {options ? <Items options={options} accountId={accountId} /> : <LoadingItems />}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
